import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Heading,
  Spinner,
  Text,
  SimpleGrid,
  Flex,
  useColorModeValue,
  useColorMode,
  useToast,
  Icon,
  Badge,
  HStack,
  VStack,
  Select,
  Button,
  Wrap,
  Input,
} from "@chakra-ui/react";
import axios from "axios";
import { Pie, Bar, Doughnut, Scatter } from "react-chartjs-2";
import { getToken } from "utils/auth";
import {
  FaUserInjured,
  FaUsers,
  FaClock,
  FaFilter,
  FaDownload,
  FaDna,
  FaUndo,
  FaLayerGroup,
  FaMapMarkerAlt,
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  ScatterController,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import config from "config";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  ScatterController,
  zoomPlugin
);

export const XP_GROUP_COLORS = {
  XPC: "#0d9488", // Teal
  XPA: "#8b5cf6", // Purple
  XPV: "#0284c7", // Sky Blue
  XPD: "#f59e0b", // Amber
  XPE: "#ec4899", // Pink
  XPF: "#10b981", // Emerald
  XPB: "#6366f1", // Indigo
  XP: "#0d9488",  // Default XP
  Unknown: "#94a3b8", // Slate
};

const Statistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [allFilterOptions, setAllFilterOptions] = useState({
    distinct_xp_groups: [],
    distinct_regions: [],
  });
  const [loading, setLoading] = useState(true);

  // Multi-Select Combination Filter States
  const [selectedXPGroups, setSelectedXPGroups] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [filterConsanguinity, setFilterConsanguinity] = useState("all");
  const [filterSex, setFilterSex] = useState("all");

  // Age Segment Filter States for Diagnostic Delay Scatter Plot
  const [ageSegment, setAgeSegment] = useState({ min: null, max: null, preset: "all" });
  const [ageTarget, setAgeTarget] = useState("both"); // "both", "onset", or "diagnosis"

  const diagnosticChartRef = useRef(null);
  const { colorMode } = useColorMode();
  const toast = useToast();

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const filterBg = useColorModeValue("slate.50", "navy.800");
  const gridLineColor = useColorModeValue("slate.200", "whiteAlpha.100");
  const pillBg = useColorModeValue("white", "navy.800");
  const rowHoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50");

  // Fetch graphs data from backend with active query filters
  const fetchGraphData = useCallback(async (filters) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();

      if (filters.xpGroups && filters.xpGroups.length > 0) {
        params.append("xp_groups", filters.xpGroups.join(","));
      }
      if (filters.regions && filters.regions.length > 0) {
        params.append("regions", filters.regions.join(","));
      }
      if (filters.consanguinity && filters.consanguinity !== "all") {
        params.append("consanguinity", filters.consanguinity);
      }
      if (filters.sex && filters.sex !== "all") {
        params.append("sex", filters.sex);
      }

      const res = await axios.get(`${config.API_URL}/stats/graphs?${params.toString()}`, { headers });
      setGraphData(res.data);

      if (res.data?.filter_options) {
        setAllFilterOptions((prev) => ({
          distinct_xp_groups:
            prev.distinct_xp_groups.length > 0
              ? prev.distinct_xp_groups
              : res.data.filter_options.distinct_xp_groups || [],
          distinct_regions:
            prev.distinct_regions.length > 0
              ? prev.distinct_regions
              : res.data.filter_options.distinct_regions || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching graph data:", error);
    }
  }, []);

  // Initial Data Fetch
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [statsRes, graphRes] = await Promise.all([
          axios.get(`${config.API_URL}/stats`, { headers }),
          axios.get(`${config.API_URL}/stats/graphs`, { headers }),
        ]);
        setStatistics(statsRes.data);
        setGraphData(graphRes.data);

        if (graphRes.data?.filter_options) {
          setAllFilterOptions({
            distinct_xp_groups: graphRes.data.filter_options.distinct_xp_groups || [],
            distinct_regions: graphRes.data.filter_options.distinct_regions || [],
          });
        }
      } catch (error) {
        console.error("Error fetching statistics dataset:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitial();
  }, []);

  // Re-fetch graph data when filters change
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchGraphData({
      xpGroups: selectedXPGroups,
      regions: selectedRegions,
      consanguinity: filterConsanguinity,
      sex: filterSex,
    });
  }, [selectedXPGroups, selectedRegions, filterConsanguinity, filterSex, fetchGraphData]);

  // Extract distinct filter options
  const distinctXPGroups = useMemo(() => {
    return allFilterOptions.distinct_xp_groups.length > 0
      ? allFilterOptions.distinct_xp_groups
      : ["XPC", "XPA", "XPV", "XPD", "XPB", "XPE", "XPF", "XPG"];
  }, [allFilterOptions.distinct_xp_groups]);

  const distinctRegions = useMemo(() => {
    return allFilterOptions.distinct_regions;
  }, [allFilterOptions.distinct_regions]);

  // Filter Toggles
  const toggleXPGroup = (grp) => {
    setSelectedXPGroups((prev) =>
      prev.includes(grp) ? prev.filter((g) => g !== grp) : [...prev, grp]
    );
  };

  const toggleRegion = (reg) => {
    setSelectedRegions((prev) =>
      prev.includes(reg) ? prev.filter((r) => r !== reg) : [...prev, reg]
    );
  };

  const resetFilters = () => {
    setSelectedXPGroups([]);
    setSelectedRegions([]);
    setFilterConsanguinity("all");
    setFilterSex("all");
  };

  // Diagnostic Delay Data for Scatter Plot (Onset vs Diagnosis Age)
  const diagnosticDelayData = useMemo(() => {
    if (!graphData || !graphData.delay_curve) {
      return {
        avgDelay: "—",
        minX: 0,
        maxX: 20,
        minY: 0,
        maxY: 20,
        stepSizeX: 5,
        stepSizeY: 5,
        stats: { lt: 0, eq: 0, gt: 0, total: 0 },
        datasets: [],
      };
    }

    const minFilter = ageSegment.min;
    const maxFilter = ageSegment.max;

    const filterPt = (p) => {
      let targetVal = p.x;
      if (ageTarget === "diagnosis") targetVal = p.y;

      if (ageTarget === "both") {
        if (minFilter !== null && (p.x < minFilter || p.y < minFilter)) return false;
        if (maxFilter !== null && (p.x > maxFilter || p.y > maxFilter)) return false;
      } else {
        if (minFilter !== null && targetVal < minFilter) return false;
        if (maxFilter !== null && targetVal > maxFilter) return false;
      }
      return true;
    };

    const ptsDelay = (graphData.delay_curve.points_delay || []).filter(filterPt);
    const ptsImmediate = (graphData.delay_curve.points_immediate || []).filter(filterPt);
    const ptsEarly = (graphData.delay_curve.points_early || []).filter(filterPt);

    let totalDelayYears = 0;
    ptsDelay.forEach((p) => {
      totalDelayYears += p.delay;
    });
    const validDelayCount = ptsDelay.length + ptsImmediate.length;
    const avgDelay =
      validDelayCount > 0
        ? (totalDelayYears / validDelayCount).toFixed(1)
        : graphData.summary?.avg_delay || "—";

    const allPts = [...ptsDelay, ...ptsImmediate, ...ptsEarly];
    const hasData = allPts.length > 0;
    const allX = allPts.map((p) => p.x);
    const allY = allPts.map((p) => p.y);
    const maxDataX = hasData ? Math.max(...allX) : 5;
    const minDataX = hasData ? Math.min(...allX) : 0;
    const maxDataY = hasData ? Math.max(...allY) : 5;
    const minDataY = hasData ? Math.min(...allY) : 0;
    const maxCombined = Math.max(maxDataX, maxDataY);

    let minX = 0;
    let maxX = 20;
    let minY = 0;
    let maxY = 20;

    if (ageSegment.preset === "all" && minFilter === null && maxFilter === null) {
      minX = 0;
      minY = 0;
      const targetMax = Math.min(Math.max(maxCombined, 15), 65);
      const step = targetMax <= 15 ? 2 : targetMax <= 30 ? 5 : 10;
      maxX = Math.ceil(targetMax / step) * step;
      maxY = maxX;
    } else {
      if (ageTarget === "both") {
        minX = minFilter !== null ? minFilter : 0;
        minY = minX;
        if (maxFilter !== null) {
          maxX = maxFilter;
          maxY = maxFilter;
        } else {
          const targetMax = Math.max(minX + 5, Math.ceil(maxCombined / 5) * 5);
          maxX = targetMax;
          maxY = targetMax;
        }
      } else if (ageTarget === "onset") {
        minX = minFilter !== null ? minFilter : 0;
        maxX = maxFilter !== null ? maxFilter : Math.max(minX + 5, Math.ceil(maxDataX / 5) * 5);
        minY = Math.min(minX, hasData ? Math.floor(minDataY) : 0);
        const yTop = hasData ? Math.max(maxX, maxDataY) : maxX;
        const stepY = yTop <= 15 ? 2 : yTop <= 30 ? 5 : 10;
        maxY = Math.ceil(yTop / stepY) * stepY;
      } else {
        minY = minFilter !== null ? minFilter : 0;
        maxY = maxFilter !== null ? maxFilter : Math.max(minY + 5, Math.ceil(maxDataY / 5) * 5);
        minX = Math.min(minY, hasData ? Math.floor(minDataX) : 0);
        const xTop = hasData ? Math.max(maxY, maxDataX) : maxY;
        const stepX = xTop <= 15 ? 2 : xTop <= 30 ? 5 : 10;
        maxX = Math.ceil(xTop / stepX) * stepX;
      }
    }

    const rangeX = Math.max(1, maxX - minX);
    const stepSizeX = rangeX <= 3 ? 0.5 : rangeX <= 6 ? 1 : rangeX <= 15 ? 2 : rangeX <= 30 ? 5 : 10;

    const rangeY = Math.max(1, maxY - minY);
    const stepSizeY = rangeY <= 3 ? 0.5 : rangeY <= 6 ? 1 : rangeY <= 15 ? 2 : rangeY <= 30 ? 5 : 10;

    const diagStart = Math.max(minX, minY);
    const diagEnd = Math.min(maxX, maxY);
    const diagLineData =
      diagStart < diagEnd
        ? [
          { x: diagStart, y: diagStart },
          { x: diagEnd, y: diagEnd },
        ]
        : [
          { x: minX, y: minY },
          { x: maxX, y: maxY },
        ];

    return {
      avgDelay,
      minX,
      maxX,
      minY,
      maxY,
      stepSizeX,
      stepSizeY,
      stats: {
        lt: ptsDelay.length,
        eq: ptsImmediate.length,
        gt: ptsEarly.length,
        total: ptsDelay.length + ptsImmediate.length + ptsEarly.length,
      },
      datasets: [
        {
          label: `Diagnostic Delay (x < y)`,
          data: ptsDelay,
          backgroundColor: "rgba(139, 92, 246, 0.75)",
          borderColor: "#7c3aed",
          borderWidth: 1.5,
          pointRadius: 6,
          pointHoverRadius: 9,
          pointStyle: "circle",
          order: 1,
        },
        {
          label: `Same-Age Diagnosis (x = y)`,
          data: ptsImmediate,
          backgroundColor: "rgba(16, 185, 129, 0.8)",
          borderColor: "#059669",
          borderWidth: 1.5,
          pointRadius: 6.5,
          pointHoverRadius: 9.5,
          pointStyle: "circle",
          order: 2,
        },
        {
          label: `Pre-symptomatic (x > y)`,
          data: ptsEarly,
          backgroundColor: "rgba(244, 63, 94, 0.8)",
          borderColor: "#e11d48",
          borderWidth: 1.5,
          pointRadius: 6,
          pointHoverRadius: 9,
          pointStyle: "circle",
          order: 3,
        },
        {
          type: "line",
          label: "x = y Line (Zero Delay)",
          data: diagLineData,
          borderColor: "rgba(100, 116, 139, 0.75)",
          borderWidth: 2,
          borderDash: [6, 6],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          showLine: true,
          pointStyle: "line",
          order: 99,
        },
      ],
    };
  }, [graphData, ageSegment, ageTarget]);

  // Consanguinity Chart Data (from backend pre-aggregated stats)
  const consanguinityChartData = useMemo(() => {
    const list = graphData?.consanguinity_chart || [];
    const labels = list.map((item) => item.group);

    if (filterConsanguinity === "all") {
      return {
        title: "Consanguinity Distribution by XP Group",
        labels,
        stacked: true,
        datasets: [
          {
            label: "Consanguineous (Yes)",
            data: list.map((i) => i.yes ?? i.consang ?? 0),
            backgroundColor: "#0d9488",
            borderRadius: 6,
          },
          {
            label: "Non-Consanguineous (No)",
            data: list.map((i) => i.no ?? i.non_consang ?? 0),
            backgroundColor: "#0284c7",
            borderRadius: 6,
          },
          {
            label: "Unknown Status",
            data: list.map((i) => i.unknown ?? 0),
            backgroundColor: "#94a3b8",
            borderRadius: 6,
          },
        ],
      };
    } else {
      const activeData = list.map((i) => {
        if (filterConsanguinity === "Yes") return i.yes ?? i.consang ?? 0;
        if (filterConsanguinity === "No") return i.no ?? i.non_consang ?? 0;
        return i.unknown ?? 0;
      });
      return {
        title: `XP Group Prevalence (${filterConsanguinity === "Yes"
            ? "Consanguineous Cohort"
            : filterConsanguinity === "No"
              ? "Non-Consanguineous Cohort"
              : "Unknown Consanguinity"
          })`,
        labels,
        stacked: false,
        datasets: [
          {
            label: `Cases (${filterConsanguinity})`,
            data: activeData,
            backgroundColor: filterConsanguinity === "Yes" ? "#0d9488" : "#0284c7",
            borderRadius: 8,
          },
        ],
      };
    }
  }, [graphData, filterConsanguinity]);

  // Recurrent Mutation Hotspots (from backend pre-aggregated hotspots)
  const mutationHotspotData = useMemo(() => {
    const hotspots = graphData?.mutation_hotspots || [];
    const sortedList = hotspots.map((item) => ({
      key: item.key,
      count: item.count,
      percentage: item.percentage,
      gene: item.gene || "XP",
      cDNA: item.cDNA || "—",
      protein: item.protein || "—",
    }));

    const totalCases = sortedList.reduce((sum, item) => sum + item.count, 0);

    const groupCounts = {};
    sortedList.forEach((item) => {
      const g = item.gene || "XP";
      groupCounts[g] = (groupCounts[g] || 0) + item.count;
    });

    const groupKeys = Object.entries(groupCounts).map(([gene, count]) => ({
      gene,
      count,
      color: XP_GROUP_COLORS[gene] || "#8b5cf6",
    }));

    const labels = sortedList.map((item) => {
      const varParts = [];
      if (item.cDNA && item.cDNA !== "—") varParts.push(item.cDNA);
      if (item.protein && item.protein !== "—") varParts.push(item.protein);
      const varStr = varParts.join(" ");
      return item.gene ? `${item.gene}: ${varStr}` : varStr;
    });

    return {
      labels,
      sortedList,
      groupKeys,
      totalCases,
      datasets: [
        {
          label: "Identified Patients",
          data: sortedList.map((item) => item.count),
          backgroundColor: sortedList.map((item) => XP_GROUP_COLORS[item.gene] || "#8b5cf6"),
          borderColor: sortedList.map((item) => XP_GROUP_COLORS[item.gene] || "#8b5cf6"),
          borderWidth: 1,
          borderRadius: 8,
          hoverBackgroundColor: sortedList.map((item) => XP_GROUP_COLORS[item.gene] || "#8b5cf6"),
        },
      ],
    };
  }, [graphData]);

  // Dynamic Scale & Ticks for Hotspot Bar Chart
  const hotspotChartConfig = useMemo(() => {
    const list = mutationHotspotData.sortedList || [];
    const maxVal = Math.max(1, ...(list.map((i) => i.count) || [1]));
    const targetMax = maxVal * 1.25;
    let step = 10;
    if (targetMax <= 10) step = 2;
    else if (targetMax <= 25) step = 5;
    else if (targetMax <= 60) step = 10;
    else if (targetMax <= 125) step = 25;
    else step = 50;

    const axisMax = Math.ceil(targetMax / step) * step;
    const ticks = [];
    for (let t = 0; t <= axisMax; t += step) {
      ticks.push(t);
    }
    return { axisMax, ticks };
  }, [mutationHotspotData.sortedList]);

  // Export De-Identified Dataset as CSV
  const handleExportCSV = () => {
    if (!graphData) return;

    const headers = [
      "Complementation Group",
      "Age at Onset (Years)",
      "Age at Diagnosis (Years)",
      "Diagnostic Delay (Years)",
    ];

    const allPts = [
      ...(graphData.delay_curve?.points_delay || []),
      ...(graphData.delay_curve?.points_immediate || []),
      ...(graphData.delay_curve?.points_early || []),
    ];

    const rows = allPts.map((pt) => [
      `"${pt.xp_group || pt.xpGroup || "XP"}"`,
      pt.x ?? "",
      pt.y ?? "",
      pt.delay ?? "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `XP_Cohort_Delay_Analytics_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Diagnostic Delay Scatter Plot as PNG
  const handleExportDelayPNG = useCallback(() => {
    let canvas = null;
    const chartRef = diagnosticChartRef.current;
    if (chartRef) {
      if (chartRef.canvas instanceof HTMLCanvasElement) {
        canvas = chartRef.canvas;
      } else if (chartRef.chartInstance?.canvas instanceof HTMLCanvasElement) {
        canvas = chartRef.chartInstance.canvas;
      } else if (chartRef.ctx?.canvas instanceof HTMLCanvasElement) {
        canvas = chartRef.ctx.canvas;
      } else if (chartRef instanceof HTMLCanvasElement) {
        canvas = chartRef;
      }
    }
    if (!canvas) {
      canvas = document.querySelector("#diagnostic-delay-chart canvas");
    }

    if (!canvas) {
      toast({
        title: "Export failed",
        description: "Chart canvas could not be found. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = canvas.width;
      exportCanvas.height = canvas.height;
      const ctx = exportCanvas.getContext("2d");

      // Solid background matching theme (no transparent cutouts)
      const isDark = colorMode === "dark";
      ctx.fillStyle = isDark ? "#0b1329" : "#ffffff";
      ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

      // Draw the chart canvas content directly (no title or extra header)
      ctx.drawImage(canvas, 0, 0);

      // Download file
      const dateStr = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.download = `XP_Diagnostic_Delay_Onset_vs_Diagnosis_${dateStr}.png`;
      link.href = exportCanvas.toDataURL("image/png", 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export successful",
        description: "Diagnostic Delay chart exported as high-resolution PNG.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to export chart PNG:", err);
      toast({
        title: "Export error",
        description: "An error occurred while generating the PNG image.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [colorMode, diagnosticDelayData, toast]);

  const activeFilterCount =
    selectedXPGroups.length +
    selectedRegions.length +
    (filterConsanguinity !== "all" ? 1 : 0) +
    (filterSex !== "all" ? 1 : 0);

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="500px">
        <Spinner size="xl" color="teal.500" thickness="4px" />
      </Flex>
    );
  }

  const totalRecords = graphData?.summary?.total_records ?? 0;
  const cohortPercentage = graphData?.summary?.cohort_percentage ?? 0;
  const consanguinityRate = graphData?.summary?.consanguinity_rate ?? 0;
  const genotypedCount = graphData?.summary?.genotyped_count ?? 0;

  return (
    <Flex direction="column" w="100%" gap={{ base: "12px", md: "24px" }}>
      <Box
        p={{ base: "8px 6px", sm: "14px 10px", md: "22px" }}
        bg={cardBg}
        borderRadius={{ base: "12px", sm: "16px", md: "22px" }}
        border="1px solid"
        borderColor={cardBorder}
        shadow="sm"
        w="100%"
      >
        {/* Header Title & Export Button */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          mb="16px"
          gap="12px"
        >
          <Box flex="1">
            <Flex align="center" gap="8px" mb="4px" wrap="wrap">
              <Heading size={{ base: "md", md: "lg" }} color={textColor} fontWeight="800" letterSpacing="-0.02em">
                XP in numbers
              </Heading>
              <Badge colorScheme="teal" borderRadius="full" px="10px">
                Cohort Analytics
              </Badge>
            </Flex>
            <Text fontSize={{ base: "xs", sm: "sm" }} color={subtitleColor}>
              Comprehensive epidemiological, demographic, and genetic analytics of the Tunisian Xeroderma Pigmentosum cohort ({totalRecords} records analyzed)
            </Text>
          </Box>

          <Button
            colorScheme="teal"
            leftIcon={<FaDownload />}
            borderRadius="12px"
            onClick={handleExportCSV}
            size="sm"
            px="16px"
            py="18px"
            w={{ base: "100%", md: "auto" }}
            flexShrink="0"
          >
            Export Filtered CSV
          </Button>
        </Flex>

        {/* Multi-Parametric Combination Filter Panel */}
        <Box
          p={{ base: "10px 8px", sm: "14px 10px", md: "18px" }}
          bg={filterBg}
          borderRadius={{ base: "10px", sm: "14px", md: "18px" }}
          border="1px solid"
          borderColor={cardBorder}
          mb={{ base: "16px", md: "24px" }}
        >
          <Flex align="center" justify="space-between" mb="14px" wrap="wrap" gap="10px">
            <HStack spacing="8px">
              <Icon as={FaFilter} color="teal.500" />
              <Text
                fontSize="xs"
                fontWeight="700"
                color={textColor}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                Multi-Parametric Cohort Filters
              </Text>
              {activeFilterCount > 0 && (
                <Badge colorScheme="teal" borderRadius="full">
                  {activeFilterCount} Active Filters
                </Badge>
              )}
            </HStack>
            {activeFilterCount > 0 && (
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                leftIcon={<FaUndo />}
                onClick={resetFilters}
              >
                Reset All Filters
              </Button>
            )}
          </Flex>

          <VStack align="stretch" spacing="16px">
            {/* Multi-Select XP Complementation Groups */}
            <Box>
              <HStack spacing="6px" mb="8px">
                <Icon as={FaLayerGroup} color="teal.500" boxSize="12px" />
                <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase">
                  XP Complementation Groups (Select Multiple):
                </Text>
              </HStack>
              <Wrap spacing="8px">
                <Button
                  size="xs"
                  borderRadius="full"
                  variant={selectedXPGroups.length === 0 ? "solid" : "outline"}
                  colorScheme="teal"
                  onClick={() => setSelectedXPGroups([])}
                >
                  All XP Groups
                </Button>
                {distinctXPGroups.map((grp) => {
                  const isSelected = selectedXPGroups.includes(grp);
                  return (
                    <Button
                      key={grp}
                      size="xs"
                      borderRadius="full"
                      variant={isSelected ? "solid" : "outline"}
                      colorScheme="teal"
                      onClick={() => toggleXPGroup(grp)}
                    >
                      {grp}
                    </Button>
                  );
                })}
              </Wrap>
            </Box>

            {/* Multi-Select Geographical Regions */}
            <Box>
              <HStack spacing="6px" mb="8px">
                <Icon as={FaMapMarkerAlt} color="teal.500" boxSize="12px" />
                <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase">
                  Geographical Origin (Select Multiple):
                </Text>
              </HStack>
              <Wrap spacing="8px" maxH="120px" overflowY="auto">
                <Button
                  size="xs"
                  borderRadius="full"
                  variant={selectedRegions.length === 0 ? "solid" : "outline"}
                  colorScheme="cyan"
                  onClick={() => setSelectedRegions([])}
                >
                  All Regions
                </Button>
                {distinctRegions.map((reg) => {
                  const isSelected = selectedRegions.includes(reg);
                  return (
                    <Button
                      key={reg}
                      size="xs"
                      borderRadius="full"
                      variant={isSelected ? "solid" : "outline"}
                      colorScheme="cyan"
                      onClick={() => toggleRegion(reg)}
                    >
                      {reg}
                    </Button>
                  );
                })}
              </Wrap>
            </Box>

            {/* Dropdown Selects: Consanguinity & Sex */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
              <Select
                size="sm"
                borderRadius="10px"
                bg={cardBg}
                borderColor={cardBorder}
                value={filterConsanguinity}
                onChange={(e) => setFilterConsanguinity(e.target.value)}
              >
                <option value="all">Consanguinity: All Statuses</option>
                <option value="Yes">Consanguineous Only</option>
                <option value="No">Non-Consanguineous Only</option>
                <option value="Unknown">Unknown Status Only</option>
              </Select>

              <Select
                size="sm"
                borderRadius="10px"
                bg={cardBg}
                borderColor={cardBorder}
                value={filterSex}
                onChange={(e) => setFilterSex(e.target.value)}
              >
                <option value="all">Gender: Male & Female</option>
                <option value="M">Male Patients Only</option>
                <option value="F">Female Patients Only</option>
              </Select>
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Top Smart Stat Widgets */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={{ base: "10px", md: "18px" }} mb={{ base: "16px", md: "24px" }}>
          <Box
            p={{ base: "12px 10px", sm: "16px", md: "20px" }}
            bg={useColorModeValue("red.50", "whiteAlpha.05")}
            borderRadius={{ base: "12px", sm: "16px", md: "18px" }}
            border="1px solid"
            borderColor={useColorModeValue("red.200", "whiteAlpha.100")}
          >
            <HStack justify="space-between" mb="8px">
              <Text fontSize="xs" fontWeight="700" color="red.500" textTransform="uppercase">
                Active Subset
              </Text>
              <Icon as={FaUserInjured} color="red.500" boxSize="18px" />
            </HStack>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800" color={textColor}>
              {totalRecords}
            </Text>
            <Text fontSize="xs" color={subtitleColor} mt="2px">
              {cohortPercentage}% of total cohort
            </Text>
          </Box>

          <Box
            p={{ base: "12px 10px", sm: "16px", md: "20px" }}
            bg={useColorModeValue("teal.50", "whiteAlpha.05")}
            borderRadius={{ base: "12px", sm: "16px", md: "18px" }}
            border="1px solid"
            borderColor={useColorModeValue("teal.200", "whiteAlpha.100")}
          >
            <HStack justify="space-between" mb="8px">
              <Text fontSize="xs" fontWeight="700" color="teal.500" textTransform="uppercase">
                Avg Diag. Delay
              </Text>
              <Icon as={FaClock} color="teal.500" boxSize="18px" />
            </HStack>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800" color={textColor}>
              {diagnosticDelayData.avgDelay} <Text as="span" fontSize="md">yrs</Text>
            </Text>
            <Text fontSize="xs" color={subtitleColor} mt="2px">
              Onset to diagnosis lag
            </Text>
          </Box>

          <Box
            p={{ base: "12px 10px", sm: "16px", md: "20px" }}
            bg={useColorModeValue("purple.50", "whiteAlpha.05")}
            borderRadius={{ base: "12px", sm: "16px", md: "18px" }}
            border="1px solid"
            borderColor={useColorModeValue("purple.200", "whiteAlpha.100")}
          >
            <HStack justify="space-between" mb="8px">
              <Text fontSize="xs" fontWeight="700" color="purple.500" textTransform="uppercase">
                Consanguinity
              </Text>
              <Icon as={FaUsers} color="purple.500" boxSize="18px" />
            </HStack>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800" color={textColor}>
              {filterConsanguinity === "all"
                ? `${consanguinityRate}%`
                : `${totalRecords} cases`}
            </Text>
            <Text fontSize="xs" color={subtitleColor} mt="2px">
              {filterConsanguinity === "all"
                ? "Parental consanguinity ratio"
                : `Status: ${filterConsanguinity}`}
            </Text>
          </Box>

          <Box
            p={{ base: "12px 10px", sm: "16px", md: "20px" }}
            bg={useColorModeValue("blue.50", "whiteAlpha.05")}
            borderRadius={{ base: "12px", sm: "16px", md: "18px" }}
            border="1px solid"
            borderColor={useColorModeValue("blue.200", "whiteAlpha.100")}
          >
            <HStack justify="space-between" mb="8px">
              <Text fontSize="xs" fontWeight="700" color="blue.500" textTransform="uppercase">
                Genotyped Cases
              </Text>
              <Icon as={FaDna} color="blue.500" boxSize="18px" />
            </HStack>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800" color={textColor}>
              {genotypedCount}
            </Text>
            <Text fontSize="xs" color={subtitleColor} mt="2px">
              Molecular group assigned
            </Text>
          </Box>
        </SimpleGrid>

        {/* Visualizations Grid */}
        <VStack spacing={{ base: "16px", md: "24px" }} align="stretch">
          {/* SECTION 1: SIMPLE DEMOGRAPHICS & COHORT BASELINE */}
          <Box>
            <HStack spacing="8px" mb="12px">
              <Badge colorScheme="teal" px="10px" py="2px" borderRadius="full">
                Baseline
              </Badge>
              <Heading size="md" color={textColor}>
                1. Cohort Baseline Demographics
              </Heading>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: "12px", md: "20px" }}>
              {/* Chart 1: Sex Distribution Doughnut */}
              <Box
                p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
                bg={filterBg}
                borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
                border="1px solid"
                borderColor={cardBorder}
              >
                <Heading size="sm" mb="16px" color={textColor}>
                  Sex Distribution (Patients Ratio)
                </Heading>
                <Box maxW="240px" mx="auto">
                  <Doughnut
                    data={{
                      labels: ["Male Patients", "Female Patients"],
                      datasets: [
                        {
                          data: [
                            graphData?.gender_distribution?.M ?? 0,
                            graphData?.gender_distribution?.F ?? 0,
                          ],
                          backgroundColor: ["#0284c7", "#ec4899"],
                          borderWidth: 0,
                        },
                      ],
                    }}
                  />
                </Box>
              </Box>

              {/* Chart 2: Affected vs Healthy Split */}
              {statistics && (
                <Box
                  p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
                  bg={filterBg}
                  borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
                  border="1px solid"
                  borderColor={cardBorder}
                >
                  <Heading size="sm" mb="16px" color={textColor}>
                    Affected vs. Healthy
                  </Heading>
                  <Box maxW="240px" mx="auto">
                    <Pie
                      data={{
                        labels: ["Affected", "Healthy"],
                        datasets: [
                          {
                            data: [
                              statistics.patient_count || 0,
                              statistics.non_patient_count || 0,
                            ],
                            backgroundColor: ["#f43f5e", "#10b981"],
                            borderWidth: 0,
                          },
                        ],
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Chart 3: Age Cohorts Bar Chart */}
              {statistics && (
                <Box
                  p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
                  bg={filterBg}
                  borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
                  border="1px solid"
                  borderColor={cardBorder}
                >
                  <Heading size="sm" mb="16px" color={textColor}>
                    Database Age Cohorts Distribution
                  </Heading>
                  <Bar
                    data={{
                      labels: Object.keys(statistics.age_groups || {}),
                      datasets: [
                        {
                          label: "Age Group Count",
                          data: Object.values(statistics.age_groups || {}),
                          backgroundColor: "#0d9488",
                          borderRadius: 8,
                        },
                      ],
                    }}
                    options={{
                      plugins: { legend: { display: false } },
                    }}
                  />
                </Box>
              )}
            </SimpleGrid>
          </Box>

          {/* SECTION 2: GENETIC DISTRIBUTION & RECURRENT HOTSPOTS */}
          <Box>
            <HStack spacing="8px" mb="12px">
              <Badge colorScheme="blue" px="10px" py="2px" borderRadius="full">
                Genetics
              </Badge>
              <Heading size="md" color={textColor}>
                2. Genetic Distribution & Recurrent Hotspots
              </Heading>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: "12px", md: "20px" }}>
              {/* Chart 4: XP Group Distribution */}
              <Box
                p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
                bg={filterBg}
                borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
                border="1px solid"
                borderColor={cardBorder}
              >
                <Heading size="sm" mb="16px" color={textColor}>
                  XP Complementation Groups Breakdown
                </Heading>
                <Bar
                  data={{
                    labels: (graphData?.xp_group_distribution || []).map((i) => i.group),
                    datasets: [
                      {
                        label: "Patients Count",
                        data: (graphData?.xp_group_distribution || []).map((i) => i.count),
                        backgroundColor: (graphData?.xp_group_distribution || []).map(
                          (i) => XP_GROUP_COLORS[i.group] || "#0d9488"
                        ),
                        borderRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y",
                    plugins: { legend: { display: false } },
                  }}
                />
              </Box>

              {/* Chart 5: Consanguinity Profile by XP Group */}
              <Box
                p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
                bg={filterBg}
                borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
                border="1px solid"
                borderColor={cardBorder}
              >
                <Heading size="sm" mb="4px" color={textColor}>
                  {consanguinityChartData.title}
                </Heading>
                <Text fontSize="xs" color={subtitleColor} mb="16px">
                  Parental consanguinity profile across complementation groups.
                </Text>
                <Box h={{ base: "280px", sm: "300px", md: "320px" }}>
                  <Bar
                    data={consanguinityChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                          labels: {
                            boxWidth: 12,
                            padding: 8,
                            font: { size: 11, weight: "600" },
                          },
                        },
                      },
                      layout: {
                        padding: { top: 6 },
                      },
                      scales: {
                        x: {
                          stacked: consanguinityChartData.stacked,
                          grid: { display: false },
                          ticks: { font: { size: 11, weight: "600" } },
                        },
                        y: {
                          stacked: consanguinityChartData.stacked,
                          ticks: { precision: 0, font: { size: 11 } },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>

            {/* Chart 6: Recurrent Mutation Hotspots */}
            <Box
              p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
              bg={filterBg}
              borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
              border="1px solid"
              borderColor={cardBorder}
              mt={{ base: "12px", md: "20px" }}
              w="100%"
              maxW="100%"
              overflow="hidden"
            >
              <Flex
                justify="space-between"
                align={{ base: "flex-start", sm: "center" }}
                mb="14px"
                direction={{ base: "column", sm: "row" }}
                gap="10px"
              >
                <Box>
                  <Heading size="sm" color={textColor}>
                    Recurrent Mutation Hotspots (cDNA / Protein)
                  </Heading>
                  <Text fontSize="xs" color={subtitleColor} mt="2px">
                    Prevalent founder and recurrent cDNA and protein variants across the cohort.
                  </Text>
                </Box>

              </Flex>

              {mutationHotspotData.sortedList && mutationHotspotData.sortedList.length > 0 ? (
                <>
                  {/* MOBILE VIEW: Stacked Variant Cards with Full-Width Progress Bars (No horizontal scroll, fully readable strings) */}
                  <Box display={{ base: "block", md: "none" }}>
                    <VStack spacing="8px" align="stretch">
                      {mutationHotspotData.sortedList.map((item, idx) => {
                        const barWidthPct = (item.count / (hotspotChartConfig.axisMax || 1)) * 100;
                        const color = XP_GROUP_COLORS[item.gene] || "#8b5cf6";
                        const pct = totalRecords > 0 ? ((item.count / totalRecords) * 100).toFixed(1) : 0;

                        return (
                          <Box
                            key={idx}
                            p="10px 12px"
                            borderRadius="12px"
                            bg={cardBg}
                            border="1px solid"
                            borderColor={cardBorder}
                            transition="all 0.2s"
                            _hover={{ bg: rowHoverBg }}
                          >
                            <Flex justify="space-between" align="flex-start" gap="8px" mb="6px">
                              <HStack spacing="6px" align="flex-start" flex="1" minW="0">
                                <Badge
                                  px="6px"
                                  py="2px"
                                  borderRadius="full"
                                  bg={`${color}18`}
                                  color={color}
                                  border="1px solid"
                                  borderColor={`${color}40`}
                                  fontStyle="italic"
                                  fontWeight="800"
                                  fontSize="3xs"
                                  flexShrink="0"
                                  mt="1px"
                                >
                                  {item.gene}
                                </Badge>
                                <Box minW="0" flex="1">
                                  <Text fontFamily="mono" fontSize="xs" fontWeight="700" color={textColor} wordBreak="break-word">
                                    {item.cDNA}
                                  </Text>
                                  {item.protein && item.protein !== "—" && (
                                    <Text fontFamily="mono" fontSize="2xs" color={subtitleColor} wordBreak="break-word">
                                      {item.protein}
                                    </Text>
                                  )}
                                </Box>
                              </HStack>

                              <HStack spacing="4px" flexShrink="0" align="baseline">
                                <Text fontWeight="800" fontSize="xs" color={textColor}>
                                  {item.count}
                                </Text>
                                <Text fontSize="2xs" color={subtitleColor}>
                                  ({pct}%)
                                </Text>
                              </HStack>
                            </Flex>

                            {/* Full-width Bar */}
                            <Box w="100%" h="6px" bg={useColorModeValue("slate.100", "whiteAlpha.100")} borderRadius="full" overflow="hidden">
                              <Box
                                w={`${barWidthPct}%`}
                                h="100%"
                                bg={color}
                                borderRadius="full"
                                transition="width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* DESKTOP VIEW: Technical Multi-Column Horizontal Chart with Axis Gridlines */}
                  <Box display={{ base: "none", md: "block" }}>
                    <Box position="relative">
                      <VStack spacing="8px" align="stretch">
                        {mutationHotspotData.sortedList.map((item, idx) => {
                          const barWidthPct =
                            (item.count / (hotspotChartConfig.axisMax || 1)) * 100;
                          const color = XP_GROUP_COLORS[item.gene] || "#8b5cf6";
                          const pct =
                            totalRecords > 0
                              ? ((item.count / totalRecords) * 100).toFixed(1)
                              : 0;

                          return (
                            <Flex
                              key={idx}
                              align="center"
                              minH="36px"
                              borderRadius="8px"
                              _hover={{ bg: rowHoverBg }}
                              transition="background 0.2s"
                              px="4px"
                              title={`${item.gene}: ${item.cDNA} ${item.protein !== "—" ? item.protein : ""
                                } — ${item.count} cases (${pct}%)`}
                            >
                              {/* Y-Axis Label: Pill containing c. and p., then XP group badge */}
                              <Flex
                                align="center"
                                justify="flex-end"
                                w={{
                                  md: "380px",
                                  lg: "460px",
                                  xl: "520px",
                                }}
                                pr="12px"
                                flexShrink="0"
                                gap="8px"
                              >
                                <Flex
                                  alignItems="center"
                                  borderRadius="full"
                                  bg={pillBg}
                                  border="1px solid"
                                  borderColor={cardBorder}
                                  flex="1"
                                  minW="0"
                                  overflow="hidden"
                                  py="2px"
                                  px="8px"
                                >
                                  <Text
                                    as="span"
                                    fontFamily="mono"
                                    fontSize="xs"
                                    fontWeight="700"
                                    color={textColor}
                                    isTruncated
                                  >
                                    {item.cDNA}
                                    {item.protein && item.protein !== "—" ? ` • ${item.protein}` : ""}
                                  </Text>
                                </Flex>

                                <Box
                                  w="44px"
                                  flexShrink="0"
                                  textAlign="center"
                                >
                                  <Badge
                                    px="6px"
                                    py="2px"
                                    borderRadius="full"
                                    bg={`${color}18`}
                                    color={color}
                                    border="1px solid"
                                    borderColor={`${color}40`}
                                    fontStyle="italic"
                                    fontWeight="800"
                                    fontSize="2xs"
                                    letterSpacing="0.2px"
                                    display="inline-block"
                                    textAlign="center"
                                    w="100%"
                                  >
                                    {item.gene}
                                  </Badge>
                                </Box>
                              </Flex>

                              {/* Bar Column with Vertical Grid Lines */}
                              <Flex
                                flex="1"
                                minW="0"
                                align="center"
                                position="relative"
                                h="28px"
                                mr="18px"
                              >
                                {hotspotChartConfig.ticks.map((t) => (
                                  <Box
                                    key={t}
                                    position="absolute"
                                    left={`${(t / hotspotChartConfig.axisMax) * 100}%`}
                                    top="0"
                                    bottom="0"
                                    borderLeft="1px dashed"
                                    borderColor={gridLineColor}
                                    pointerEvents="none"
                                    zIndex="1"
                                  />
                                ))}

                                <Box
                                  w={`${barWidthPct}%`}
                                  h="20px"
                                  bg={color}
                                  borderRadius="0 6px 6px 0"
                                  transition="width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                                  position="relative"
                                  zIndex="2"
                                  _hover={{ filter: "brightness(1.15)" }}
                                />

                                <HStack
                                  spacing="4px"
                                  ml="8px"
                                  flexShrink="0"
                                  position="relative"
                                  zIndex="2"
                                >
                                  <Text fontWeight="800" fontSize="xs" color={textColor}>
                                    {item.count}
                                  </Text>
                                  <Text fontSize="2xs" color={subtitleColor}>
                                    ({pct}%)
                                  </Text>
                                </HStack>
                              </Flex>
                            </Flex>
                          );
                        })}
                      </VStack>

                      {/* X-Axis Line & Scale Ticks */}
                      <Flex
                        align="center"
                        mt="8px"
                        pt="8px"
                        borderTop="2px solid"
                        borderColor={cardBorder}
                      >
                        <Box
                          w={{
                            md: "380px",
                            lg: "460px",
                            xl: "520px",
                          }}
                          pr="12px"
                          flexShrink="0"
                          textAlign="right"
                        >
                          <Text
                            fontSize="2xs"
                            fontWeight="700"
                            color={subtitleColor}
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                          >
                            Cases
                          </Text>
                        </Box>

                        <Box flex="1" minW="0" position="relative" h="18px" mr="18px">
                          {hotspotChartConfig.ticks.map((t) => (
                            <Box
                              key={t}
                              position="absolute"
                              left={`${(t / hotspotChartConfig.axisMax) * 100}%`}
                              transform="translateX(-50%)"
                              textAlign="center"
                            >
                              <Box w="1px" h="4px" bg={subtitleColor} mx="auto" mb="2px" />
                              <Text fontSize="2xs" color={subtitleColor} fontWeight="600">
                                {t}
                              </Text>
                            </Box>
                          ))}
                        </Box>
                      </Flex>
                    </Box>
                  </Box>
                </>
              ) : (
                <Flex justify="center" align="center" py="40px">
                  <Text fontSize="sm" color={subtitleColor}>
                    No mutation hotspot variants matching current filters.
                  </Text>
                </Flex>
              )}
            </Box>
          </Box>

          {/* SECTION 3: NATURAL HISTORY & PHENOTYPIC EVOLUTION */}
          <Box>
            <HStack spacing="8px" mb="12px">
              <Badge colorScheme="purple" px="10px" py="2px" borderRadius="full">
                Natural History
              </Badge>
              <Heading size="md" color={textColor}>
                3. Diagnostic Delay: Onset vs. Diagnosis
              </Heading>
            </HStack>

            <VStack spacing={{ base: "14px", md: "20px" }} align="stretch">
              {/* Chart 7: Diagnostic Delay Scatter Plot (Onset vs Diagnosis Age) */}
              <Box
                p={{ base: "10px 8px", sm: "14px 10px", md: "20px" }}
                bg={filterBg}
                borderRadius={{ base: "12px", sm: "14px", md: "18px" }}
                border="1px solid"
                borderColor={cardBorder}
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "flex-start", md: "center" }}
                  mb="16px"
                  gap="12px"
                >
                  <Box>
                    <Heading size="sm" color={textColor}>
                      Diagnostic Delay: Onset vs. Diagnosis
                    </Heading>
                    <Text fontSize="xs" color={subtitleColor} mt="2px">
                      Scatter plot comparing patient age at first symptoms (x) versus formal clinical diagnosis (y). Points above the diagonal (x &lt; y) represent clinical diagnostic delay.
                    </Text>
                  </Box>

                  <HStack spacing="10px" align="center">
                    <Badge colorScheme="purple" px="10px" py="4px" borderRadius="8px" fontSize="xs">
                      Avg Lag: {diagnosticDelayData.avgDelay} yrs
                    </Badge>
                    <Button
                      size="xs"
                      leftIcon={<Icon as={FaDownload} />}
                      colorScheme="teal"
                      variant="solid"
                      borderRadius="8px"
                      fontWeight="700"
                      boxShadow="0 2px 6px rgba(13, 148, 136, 0.25)"
                      _hover={{
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 10px rgba(13, 148, 136, 0.35)",
                      }}
                      onClick={handleExportDelayPNG}
                    >
                      Export PNG
                    </Button>
                  </HStack>
                </Flex>

                {/* Interactive Age Segmentation Panel */}
                <Box
                  p="14px"
                  bg={cardBg}
                  borderRadius="14px"
                  border="1px solid"
                  borderColor={cardBorder}
                  mb="16px"
                >
                  <Flex
                    direction={{ base: "column", lg: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", lg: "center" }}
                    gap="12px"
                  >
                    {/* Preset Age Segment Buttons */}
                    <Box>
                      <Text fontSize="xs" fontWeight="700" color={textColor} mb="6px">
                        Age Segments (Presets):
                      </Text>
                      <Wrap spacing="6px">
                        {[
                          { label: "All Ages", preset: "all", min: null, max: null },
                          { label: "0–2 yrs (Infancy)", preset: "0-2", min: 0, max: 2 },
                          { label: "0–5 yrs (Early Childhood)", preset: "0-5", min: 0, max: 5 },
                          { label: "6–12 yrs (School Age)", preset: "6-12", min: 6, max: 12 },
                          { label: "13–18 yrs (Adolescent)", preset: "13-18", min: 13, max: 18 },
                          { label: "19+ yrs (Adult)", preset: "19+", min: 19, max: null },
                        ].map((p) => {
                          const isSelected = ageSegment.preset === p.preset;
                          return (
                            <Button
                              key={p.preset}
                              size="xs"
                              variant={isSelected ? "solid" : "outline"}
                              colorScheme={isSelected ? "teal" : "gray"}
                              borderRadius="full"
                              px="10px"
                              onClick={() =>
                                setAgeSegment({ min: p.min, max: p.max, preset: p.preset })
                              }
                            >
                              {p.label}
                            </Button>
                          );
                        })}
                      </Wrap>
                    </Box>

                    {/* Custom Segment Range & Filter Target */}
                    <Flex
                      align="center"
                      gap="8px"
                      wrap="wrap"
                      pt={{ base: "8px", lg: "0" }}
                      borderTop={{ base: "1px solid", lg: "none" }}
                      borderColor={cardBorder}
                    >
                      <Box minW="130px">
                        <Text fontSize="xs" fontWeight="700" color={textColor} mb="4px">
                          Filter by:
                        </Text>
                        <Select
                          size="xs"
                          borderRadius="8px"
                          value={ageTarget}
                          onChange={(e) => setAgeTarget(e.target.value)}
                          bg={filterBg}
                          borderColor={cardBorder}
                        >
                          <option value="both">Both (Square Zoom)</option>
                          <option value="onset">Onset Age only (x)</option>
                          <option value="diagnosis">Diagnosis Age only (y)</option>
                        </Select>
                      </Box>

                      <Box w="80px">
                        <Text fontSize="xs" fontWeight="700" color={textColor} mb="4px">
                          Min Age:
                        </Text>
                        <Input
                          size="xs"
                          type="number"
                          min="0"
                          max="100"
                          placeholder="Min (0)"
                          value={ageSegment.min !== null ? ageSegment.min : ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAgeSegment((prev) => ({
                              ...prev,
                              min: val === "" ? null : Math.max(0, Number(val)),
                              preset: "custom",
                            }));
                          }}
                          borderRadius="8px"
                          bg={filterBg}
                          borderColor={cardBorder}
                        />
                      </Box>

                      <Box w="80px">
                        <Text fontSize="xs" fontWeight="700" color={textColor} mb="4px">
                          Max Age:
                        </Text>
                        <Input
                          size="xs"
                          type="number"
                          min="0"
                          max="100"
                          placeholder="Max"
                          value={ageSegment.max !== null ? ageSegment.max : ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAgeSegment((prev) => ({
                              ...prev,
                              max: val === "" ? null : Math.max(0, Number(val)),
                              preset: "custom",
                            }));
                          }}
                          borderRadius="8px"
                          bg={filterBg}
                          borderColor={cardBorder}
                        />
                      </Box>

                      {(ageSegment.min !== null ||
                        ageSegment.max !== null ||
                        ageSegment.preset !== "all") && (
                          <Button
                            size="xs"
                            mt="20px"
                            leftIcon={<Icon as={FaUndo} />}
                            variant="ghost"
                            colorScheme="red"
                            borderRadius="8px"
                            onClick={() =>
                              setAgeSegment({ min: null, max: null, preset: "all" })
                            }
                          >
                            Reset
                          </Button>
                        )}
                    </Flex>
                  </Flex>
                </Box>

                <Box id="diagnostic-delay-chart" h={{ base: "380px", sm: "440px", md: "520px" }}>
                  <Scatter
                    key={`diag-scatter-${diagnosticDelayData.minX}-${diagnosticDelayData.maxX}-${diagnosticDelayData.minY}-${diagnosticDelayData.maxY}-${ageTarget}`}
                    ref={diagnosticChartRef}
                    data={diagnosticDelayData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          type: "linear",
                          min: 0,
                          max: diagnosticDelayData.maxX,
                          title: {
                            display: true,
                            text: "Age at Onset of Symptoms (Years) — [x]",
                            font: { weight: "600", size: 12 },
                          },
                          grid: {
                            color: "rgba(160, 174, 192, 0.15)",
                          },
                          ticks: {
                            font: { size: 11 },
                            stepSize: diagnosticDelayData.stepSizeX,
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          },
                        },
                        y: {
                          type: "linear",
                          min: 0,
                          max: diagnosticDelayData.maxY,
                          title: {
                            display: true,
                            text: "Age at Clinical Diagnosis (Years) — [y]",
                            font: { weight: "600", size: 12 },
                          },
                          grid: {
                            color: "rgba(160, 174, 192, 0.15)",
                          },
                          ticks: {
                            font: { size: 11 },
                            stepSize: diagnosticDelayData.stepSizeY,
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                          },
                        },
                      },
                      plugins: {
                        zoom: {
                          limits: {
                            x: { min: 0 },
                            y: { min: 0 },
                          },
                          pan: {
                            enabled: true,
                            mode: "xy",
                            threshold: 10,
                          },
                          zoom: {
                            wheel: { enabled: false },
                            pinch: { enabled: true },
                            drag: { enabled: false },
                            mode: "xy",
                          },
                        },
                        legend: {
                          position: "top",
                          align: "center",
                          labels: {
                            usePointStyle: true,
                            boxWidth: 10,
                            padding: 8,
                            font: { size: 11, weight: "600" },
                          },
                        },
                        tooltip: {
                          padding: 12,
                          cornerRadius: 8,
                          callbacks: {
                            title: (items) => {
                              if (!items || !items.length) return "";
                              const item = items[0];
                              const raw = item.raw;
                              if (!raw || raw.delay === undefined) {
                                return "Reference Line (x = y)";
                              }
                              return `Clinical Case (${raw.xp_group || raw.xpGroup || "XP"})`;
                            },
                            label: (context) => {
                              const raw = context.raw;
                              if (!raw) return "";
                              if (raw.delay === undefined) {
                                return `Zero Delay Diagonal: Onset = Diagnosis (${context.parsed?.x ?? ""
                                  } yrs)`;
                              }
                              const lines = [];
                              lines.push(
                                `Complementation Group: ${raw.xp_group || raw.xpGroup || "XP"}`
                              );
                              lines.push(`Onset Age (x): ${raw.x} yrs`);
                              lines.push(`Diagnosis Age (y): ${raw.y} yrs`);
                              if (raw.delay > 0) {
                                lines.push(`Diagnostic Delay: +${raw.delay} yrs (Lag)`);
                              } else if (raw.delay === 0) {
                                lines.push(`Same-Age Diagnosis: 0 yrs (x = y)`);
                              } else {
                                lines.push(
                                  `Pre-symptomatic / Early: ${raw.delay} yrs (x > y)`
                                );
                              }
                              return lines;
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
};

export default Statistics;
